import { useEffect, useState, type ReactNode } from 'react';

interface TenantLogoProps {
  logoUrl: string | null;
  /** Image classes — caller controls size + ring + radius so the same
   *  helper works for the 24px row avatar and the 48px detail header. */
  imgClassName: string;
  /** Rendered when no logoUrl is set OR when the img errors at load time. */
  fallback: ReactNode;
}

/**
 * Renders a tenant's logo with a fail-safe fallback. Without this, a 404
 * logoUrl (broken link, expired CDN, etc.) shows the browser's default
 * broken-image icon inside the admin shell — alarming and useless. Wrap
 * the swap in state so React re-renders the fallback exactly once when
 * the load fails, without trying the URL again.
 */
export function TenantLogo({ logoUrl, imgClassName, fallback }: TenantLogoProps): JSX.Element {
  const [broken, setBroken] = useState(false);

  // Reset the broken flag when the URL changes — caller may have updated
  // the logo and the new URL deserves its own try.
  useEffect(() => {
    setBroken(false);
  }, [logoUrl]);

  if (!logoUrl || broken) return <>{fallback}</>;
  return (
    <img
      src={logoUrl}
      alt=""
      className={imgClassName}
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
    />
  );
}
